import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { Route } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Routes";
import { Vaccine } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Vaccines";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { SimpleDisplayModel } from "@/Common/Components/Search/SimpleSearchAutocomplete";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CreateNoteInView } from "@/Documents/Components/DocumentView/CreateNoteInView";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { ConstantValues } from "./Immunization/Constants/ConstantValues";
import { ValueSetType } from "./Immunization/Constants/ValueSetType";
import { ImmunizationDetailsEditComponent } from "./Immunization/ImmunizationDetailsEdit";
import { ImmunizationDetailsViewComponent } from "./Immunization/ImmunizationDetailsView";
import { ImmunizationProfileController } from "./Immunization/ImmunizationProfileController";
import { CodeValueSearchStrategy } from "./Immunization/ImmunizationProfileSections/CodeValueSearchAutocompleteCustom";
import { CodeValue } from "./Immunization/Models/CodeValue";
import { MedicationBatchModel } from "./Immunization/Models/MedicalBatchModel";
import { MedicationModel } from "./Immunization/Models/MedicationModel";
import { RvqLocationPreference } from "./Immunization/Models/RvqLocationPreference";
import { RvqMedicationPreference } from "./Immunization/Models/RvqMedicationPreference";
import { RvqPractitionerPreference } from "./Immunization/Models/RvqPractitionerPreference";
import { QuebecVaccinationRegistryHelper } from "./Immunization/QuebecVaccinationRegistryHelper";
import { GetRvqMedicationByTradeNameResponse } from "./Immunization/ResponseModels/GetRvqMedicationByTradeNameRequest";
import { GetRvqPreferencesResponse } from "./Immunization/ResponseModels/GetRvqPreferencesResponse";
import { GetValuesResponse } from "./Immunization/ResponseModels/GetValuesResponse";
import { RvqImmunizationSavingSyncManager } from "./Immunization/RvqImmunizationSavingSyncManager";
import { BaseMedicalItemsView, IMedicalItemsViewProps } from "./BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";
import { MedicalItemDate } from "./MedicalItemDate";
import { RvqOptions } from "./RvqOptions";

enum ImmunizationDetailEditForm {
    CreateImmunization,
    RefuseSpecificVaccine,
    RefuseAllVaccines
}

class MedicalItemsImmunizationViewState extends BaseMedicalItemsViewState {
    private static readonly vaccinationCodes = ["IDINJ", "IM", "IVESINJ", "PO", "SQ", "PARENTINJ", "NASAL", "NASALINSTIL", "NASINHL"];

    public IsNewEMRTemplate: boolean;
    public IsNewMedicalItem: boolean;

    public AvailableVacines: SearchableItemModelT<string>[];
    public ImmunizationName: string;
    public ImmunizationNameId: string;

    public ImmunizingAgent: string;

    public IsRefused: boolean;

    public AdministrationDate: MedicalItemDate;
    public RefusalDate: MedicalItemDate;

    public Booster: number;
    public BoosterInMonths: number;
    public SelectedBoosterLookupId: string;

    public Manufacturer: string;
    public LotNumber: string;

    public AvailableRouteLookups: KeyValuePair<string, string>[];
    public RouteCode: string;

    public AvailableSitesLookups: FlatLookupView[];
    public SelectedSiteLookupId: string;

    public Dose: number;
    public AvailableDoseUnitLookups: FlatLookupView[];
    public SelectedDoseUnitLookupId: string;

    public Instruction: string;
    public Notes: string;

    public Id: string;
    public DisplayId: string;

    public SelectedAntigens: CodeValue[];
    public AntigensInCurrentAgent: CodeValue[];

    /**
     * Whether the immunization profile popup for the Quebec vaccination registry (RVQ) should be shown now.
     */
    public showImmunizationProfile: boolean;

    /**
     * Whether the user is authenticated with the Quebec vaccination registry (RVQ).
     */
    public isRvqAuthenticated: boolean;

    /**
     * Whether we're currently fetching auth info from the Quebec vaccination registry (RVQ).
     */
    public isRvqLoading: boolean;

    public AdministeredByDisplayText: string;
    public LocationDisplayText: string;
    public QuebecRegistryId: number;
    public QuebecRegistryAgentCode: string;
    public QuebecRegistryAdministeredById: number;
    public QuebecRegistryLocationId: number;
    public QuebecRegistryDoseUnitCode: string;
    public QuebecRegistrySiteCode: string;
    public QuebecRegistryLotId: number;
    public QuebecRegistryAdministeredReasonCode: string;
    public QuebecRegistryExpirationDate: Date;
    public QuebecRegistryRefusalMotiveCode: string;
    public IsQuebecRegistryForm: boolean;
    public QuebecRegistryAntigenCodes: string;
    public SubmittedToQuebecRegistryOn: Date | null;
    public SubmittedToQuebecRegistryBy: string | null;
    public IsQuebecRegistryHistoricalVaccine: boolean;
    public VaccinatorEmployeeId: string;

    public RvqOptions: RvqOptions;

    public showExpiredLotNumbers: boolean;
    public rvqMedicationPreferences: RvqMedicationPreference[];
    public rvqPractitionerPreferences: RvqPractitionerPreference[];
    public rvqLocationPreferences: RvqLocationPreference[];
    public codeValueSearchStrategy: CodeValueSearchStrategy;
    public currentForm: ImmunizationDetailEditForm;
    public shouldSyncWithRvq: boolean;
    public isCurrentlySaving: boolean;

    constructor(s?: Partial<MedicalItemsImmunizationViewState>) {
        super(s);
        Object.assign(this, s);
    }

    public static default(): MedicalItemsImmunizationViewState {
        const availableRouteLookUps = cacheStore.RoutesCache
            .filter(p => this.vaccinationCodes.contains(p.Code))
            .sortByProperty(p => p.Code)
            .map(x => new KeyValuePair(x.Code, Route.GetLocalizedDescription(x)));

        return new MedicalItemsImmunizationViewState({
            AvailableVacines: cacheStore.VaccinesCache.map(x => new SearchableItemModelT<string>(Vaccine.GetLocalizedName(x))),
            SelectedBoosterLookupId: "",
            AvailableRouteLookups: availableRouteLookUps,
            AvailableSitesLookups: cacheStore.FindLookupsByTypeCode("SITES"),
            AvailableDoseUnitLookups: cacheStore.FindLookupsByTypeCode("DosageUnit"),
            AdministrationDate: MedicalItemDate.today(),
            RefusalDate: MedicalItemDate.default(),
            RvqOptions: {
                tradeNames: [],
                dosageUnits: {},
                administrationReasons: {},
                administrationSites: {},
                batches: [],
                routes: {},
                agents: {},
                locations: [],
                practitionerRoles: {},
                vaccinationRefusalMotives: {},
                antigens: {}
            },
            rvqMedicationPreferences: [],
            rvqLocationPreferences: [],
            rvqPractitionerPreferences: [],
            SelectedAntigens: [],
            AntigensInCurrentAgent: [],
            codeValueSearchStrategy: new CodeValueSearchStrategy([]),
            currentForm: ImmunizationDetailEditForm.CreateImmunization,
            showExpiredLotNumbers: false,
            shouldSyncWithRvq: cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry,
            isCurrentlySaving: false,
            AdministeredByDisplayText: ""
        });
    }

    public static clearNonRefusalFields(s: MedicalItemsImmunizationViewState): void {
        if (s.currentForm === ImmunizationDetailEditForm.RefuseAllVaccines) {
            s.ImmunizationName = "";
            s.ImmunizationNameId = null;
            s.QuebecRegistryAgentCode = null;
            s.ImmunizingAgent = "";
        }
        s.Manufacturer = "";
        s.LotNumber = "";
        s.QuebecRegistryLotId = null;
        s.RvqOptions.batches = [];
        s.Dose = null;
        s.QuebecRegistryDoseUnitCode = "";
        s.RouteCode = "";
        s.QuebecRegistrySiteCode = "";
        s.LocationDisplayText = "";
        s.QuebecRegistryLocationId = null;
        s.QuebecRegistryAdministeredReasonCode = null;
        s.AdministeredByDisplayText = "";
        s.QuebecRegistryAdministeredById = null;
        s.VaccinatorEmployeeId = null;
        s.AdministrationDate = MedicalItemDate.default();
    }

    public static clearLinkedTradeNameFields(s: MedicalItemsImmunizationViewState): void {
        s.QuebecRegistryAdministeredReasonCode = null;
        s.ImmunizationName = "";
        s.ImmunizationNameId = null;
        s.Manufacturer = "";
        s.LotNumber = "";
        s.QuebecRegistryLotId = null;
        s.ImmunizingAgent = "";
        s.QuebecRegistryAgentCode = "";
        s.SelectedAntigens = [];
        s.AntigensInCurrentAgent = [];
        s.RvqOptions.batches = [];
        s.RouteCode = "";
        s.Dose = null;
        s.QuebecRegistryDoseUnitCode = "";
        s.codeValueSearchStrategy = new CodeValueSearchStrategy([]);
        s.QuebecRegistrySiteCode = "";
    }

    public static getDataToLock(state: MedicalItemsImmunizationViewState): Partial<MedicalItemsImmunizationViewState> {
        const lockData = {
            ImmunizationName: state.ImmunizationName,
            ImmunizingAgent: state.ImmunizingAgent,
            Manufacturer: state.Manufacturer,
            LotNumber: state.LotNumber,
            RouteCode: state.RouteCode,
            Dose: state.Dose,
            SelectedDoseUnitLookupId: state.SelectedDoseUnitLookupId,
            Booster: state.Booster,
            BoosterInMonths: state.BoosterInMonths,
            SelectedBoosterLookupId: state.SelectedBoosterLookupId
        };

        return cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry
            ? {
                ...lockData,
                QuebecRegistryAgentCode: state.QuebecRegistryAgentCode,
                QuebecRegistryDoseUnitCode: state.QuebecRegistryDoseUnitCode,
                QuebecRegistryLotId: state.QuebecRegistryLotId,
                QuebecRegistrySiteCode: state.QuebecRegistrySiteCode
            }
            : {
                ...lockData,
                SelectedSiteLookupId: state.SelectedSiteLookupId
            };
    }
}

class MedicalItemsImmunizationView extends BaseMedicalItemsView<IMedicalItemsViewProps, MedicalItemsImmunizationViewState> {
    constructor(props: IMedicalItemsViewProps) {
        super(props);
        const cacheId = this.getItemCacheId(props);
        this.state = new MedicalItemsImmunizationViewState({
            ...MedicalItemsImmunizationViewState.default(),
            ...patientStore.getMedicalItemCachedState(this.props.PatientContactId, cacheId)
        });
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        if (this.isRvqEnabled)
            this.loadRvqData();
    }

    public componentDidUpdate(prevProps: IMedicalItemsViewProps): void {
        // when the model changes, check if we have to render an item in edit mode, if so, we set the state so we can edit its values
        const model = this.props.Model;

        if (model != prevProps.Model) {
            const isEditing = this.isEditing(model);
            const medicalItems = model.get("MedicalItemsList")?.toArray() ?? [];

            if (medicalItems?.any()) {
                if (isEditing) {
                    const latestNewItem = MedicalItemModel
                        .sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id)))
                        .firstOrDefault();

                    if (latestNewItem != null) {
                        // Use the new MedicalItem
                        this.setEditedItemState(latestNewItem);
                    }
                    else {
                        // modify the last item if Uncommitted
                        const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

                        if (latestMedicalItem?.IsCommitted === false)
                            this.setEditedItemState(latestMedicalItem);
                    }
                }
                else
                    this.setViewVaccinationState(medicalItems);
            }
        }

        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.Id, this.state);
    }

    private get isRvqEnabled(): boolean {
        return cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry;
    }

    /**
     * Get whether a trade name is a recognized value from the RVQ.
     * @param tradeName The trade name to look up.
     */
    private async isValidTradeName(tradeName: string): Promise<boolean> {
        if (!tradeName)
            return false;

        // check state
        const localNames = this.state.RvqOptions?.tradeNames;
        if (localNames?.any())
            return localNames.some(p => p.Item.Id === tradeName);

        // check server
        const response = await Client.postAsync<GetValuesResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetValues,
            { Type: [ValueSetType.TradeName] }
        );

        return !!response.data?.Types?.[ValueSetType.TradeName]?.[tradeName];
    }

    private async loadRvqData(): Promise<void> {
        // perform initial login check to prepopulate components
        if (this.isRvqEnabled && !this.state.isRvqAuthenticated) {
            const isAuthenticated = await sessionStore.rvq.tryRequireAuthentication(false);
            this.onRvqAuthChanged(isAuthenticated, false);
        }

        // fetch domain values
        let issues: string[] = [];
        const response = await Client.postAsync<GetValuesResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetValues,
            {
                Type: [
                    ValueSetType.TradeName,
                    ValueSetType.DosageUnit,
                    ValueSetType.AdministrationSite,
                    ValueSetType.AdministrationReason,
                    ValueSetType.AdministrationRoute,
                    ValueSetType.Agent,
                    ValueSetType.PractitionnerRole,
                    ValueSetType.VaccinationRefusalMotive,
                    ValueSetType.Antigen
                ]
            }
        );
        if (response.data?.Issues?.any())
            issues = [...response.data.Issues];

        // fetch preferences
        const preferencesResponse = await Client.postAsync<GetRvqPreferencesResponse>(ServerOperations.Server.QuebecVaccinationRegistry_GetPreferences, {});
        const medicationPreferences = (preferencesResponse?.data?.MedicationPreferences ?? []).map(p => new RvqMedicationPreference(p));
        const pracitionerPreferences = (preferencesResponse?.data?.PractitionerPreferences ?? []).map(p => new RvqPractitionerPreference(p));
        const locationPreferences = (preferencesResponse?.data?.LocationPreferences ?? []).map(p => new RvqLocationPreference(p));
        if (issues.any())
            sessionStore.showPopup(issues);

        // fetch antigens
        let antigens = this.state.QuebecRegistryAgentCode && this.isRvqEnabled
            ? await QuebecVaccinationRegistryHelper.getAntigensByAgentCode(this.state.QuebecRegistryAgentCode) ?? []
            : [];

        // update state
        this.mutateState(s => {
            if (response.isSuccess) {
                const types = response.data.Types;
                if (types) {
                    s.RvqOptions = {
                        ...s.RvqOptions,
                        tradeNames: SimpleDisplayModel.fromDictionary(types[ValueSetType.TradeName]),
                        dosageUnits: types[ValueSetType.DosageUnit],
                        administrationSites: types[ValueSetType.AdministrationSite],
                        administrationReasons: types[ValueSetType.AdministrationReason],
                        routes: types[ValueSetType.AdministrationRoute],
                        agents: types[ValueSetType.Agent],
                        locations: [],
                        practitionerRoles: types[ValueSetType.PractitionnerRole],
                        vaccinationRefusalMotives: types[ValueSetType.VaccinationRefusalMotive],
                        antigens: types[ValueSetType.Antigen]
                    };
                }
            }
            s.codeValueSearchStrategy = QuebecVaccinationRegistryHelper.getCodeValueSearchStrategy(antigens, "CodeValueSearch-Antigen", ValueSetType.Antigen);

            if (preferencesResponse.isSuccess) {
                s.rvqMedicationPreferences = medicationPreferences;
                s.rvqPractitionerPreferences = pracitionerPreferences;
                s.rvqLocationPreferences = locationPreferences;
            }
        });
    }

    // Used for invalidation. Only trigger on latest immunization for current emrTemplate.
    private get latestCommittedMedicalItem(): MedicalItemImmunization {
        const medicalItems = this.props.Model.get("MedicalItemsList")?.toArray() ?? [];
        return MedicalItemModel
            .sortByCreationDateDesc(medicalItems.filter(x => !isGuidNullOrUndefinedOrEmpty(x.Id)))
            .firstOrDefault() as MedicalItemImmunization;
    }

    private listItemRenderFunction(immunization: MedicalItemImmunization): JSX.Element {
        const date = immunization.IsRefused
            ? immunization.RefusedDate
            : immunization.AdministeredDate;

        const dateString = date
            ? PatientMedicalItemsHelper.GetLifeStageDateString(date, immunization.AdministeredPrecisionDate)
            : "--";

        const refusedStyle = immunization.IsRefused ? "red-text" : "";

        return (
            <div className="med-note" key={immunization.Id}>
                <div className="flex-container medical-note">
                    <div className="flx-col a">
                        <p className={`ellipsis bold ${refusedStyle}`}>{QuebecVaccinationRegistryHelper.getImmunizationDisplayName(immunization)}</p>
                        {immunization.EMRTemplateIsInvalid &&
                            <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")}</p>
                        }
                        <p className="ellipsis">{dateString}</p>
                    </div>
                </div>
            </div>
        );
    }

    public render(): JSX.Element {
        return (
            <div>
                {this.state.showImmunizationProfile && (
                    <ImmunizationProfileController
                        mylePatientContactId={this.props.PatientContactId}
                        medicationPreferences={this.state.rvqMedicationPreferences}
                        practitionerPreferences={this.state.rvqPractitionerPreferences}
                        locationPreferences={this.state.rvqLocationPreferences}
                        refreshVaccinationValues={() => this.loadRvqData()}
                        onUpdatePreferences={(medicationPreferences, practitionerPreferences, locationPreferences, then) =>
                            this.mutateState(
                                s => {
                                    s.rvqMedicationPreferences = medicationPreferences?.map(p => new RvqMedicationPreference(p)) ?? s.rvqMedicationPreferences;
                                    s.rvqPractitionerPreferences = practitionerPreferences?.map(p => new RvqPractitionerPreference(p)) ?? s.rvqPractitionerPreferences;
                                    s.rvqLocationPreferences = locationPreferences?.map(p => new RvqLocationPreference(p)) ?? s.rvqLocationPreferences;
                                },
                                () => then?.()
                            )
                        }
                        onClose={() => this.mutateState(s => s.showImmunizationProfile = false)}
                        model={this.props.Model}
                    />
                )}
                <BaseMedicalItemsView
                    PatientContactId={this.props.PatientContactId}
                    Model={this.props.Model}
                    DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("ImmunizationsLabel")}
                    ListItemRenderFunction={this.listItemRenderFunction}
                    MedicalItemRenderFunction={(immunization: MedicalItemImmunization) => (
                        <ImmunizationDetailsViewComponent
                            immunization={immunization}
                            isQuebecVaccineRegistryActive={cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry}
                            rvqOptions={this.state.RvqOptions}
                        />
                    )}
                    MedicalItemEditRenderFunction={(immunization: MedicalItemImmunization) => (
                        <ImmunizationDetailsEditComponent
                            encounter={this.props.Model.get("Encounter")}
                            immunization={immunization}
                            state={this.state}
                            mutateState={(mutation, then) => this.mutateState(mutation, then)}
                            patientDateOfBirth={this.props.PatientModel?.IndividualResponse?.DateOfBirth}
                            isRvqAuthenticated={this.state.isRvqAuthenticated}
                        />
                    )}
                    SaveFunction={() => this.save()}
                    SavePlusFunction={() => this.savePlus()}
                    emrType={MedicalItemTypeEnum.Immunization}
                    CheckIfSaveEnabledFunction={() => this.isSaveButtonEnabled()}
                    toSearchableStringFunction={items => this.searchMedicalItem(items)}
                    SubTitleComponent={cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry
                        ? this.state.isRvqLoading
                            ? (
                                <div style={{ width: "40px" }}>
                                    <InformationLoadingComponent smallVersion />
                                </div>
                            )
                            : (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        this.mutateState(
                                            s => s.isRvqLoading = true,
                                            async () => {
                                                const isAuthenticated = await sessionStore.rvq.tryRequireAuthentication();
                                                this.onRvqAuthChanged(isAuthenticated, true);
                                            }
                                        );
                                    }}
                                >
                                    {cacheStore.GetString("RVQLabel")}
                                </button>
                            )
                        : null
                    }
                    CanItemBeDeleted={(medicalItem: MedicalItemImmunization) => {
                        if (!medicalItem.QuebecRegistryId)
                            return true;

                        sessionStore.showPopup({
                            title: cacheStore.GetString("WarningLabel"),
                            content: cacheStore.GetString("QuebecVaccinationRegistry_CantDeleteEntryWithoutDeletingFromRvqFirstPopupMessage")
                        });
                        return false;
                    }}
                    additionalFooterControls={this.isRvqEnabled && this.props.Model.get("ViewMode") == MedicalItemViewMode.Edit
                        ? (
                            <span className="pull-left push-right-5">
                                <CheckboxControl
                                    label={cacheStore.GetString("SyncOnSaveLabel")}
                                    labelStyle={{ margin: 0 }}
                                    onChange={checked => this.mutateState(s => s.shouldSyncWithRvq = checked)}
                                    checked={this.state.shouldSyncWithRvq}
                                />
                            </span>
                        )
                        : null
                    }
                    InvalidateComponent={(this.isRvqEnabled && this.latestCommittedMedicalItem?.QuebecRegistryId)
                        ? (
                            <CreateNoteInView
                                addNoteOperation={LocalOperations.Unassigned.Unassigned}
                                addNoteCallback={async note => {
                                    // Save reason for invalidation in RVQ related table
                                    const response = await Client.postAsync<null>(
                                        ServerOperations.Server.QuebecVaccinationRegistry_SaveDeletionReason,
                                        {
                                            MedicalItemId: this.latestCommittedMedicalItem.MedicalItemId,
                                            Reason: note
                                        }
                                    );

                                    if (response.isSuccess)
                                        sessionStore.dispatch(LocalPatientOperations.MedicalItems.SaveValidationNote, { value: note });
                                    else
                                        sessionStore.showPopup(response.errors);

                                    if (!sessionStore.rvq.canSubmitToRvq(this.latestCommittedMedicalItem, false))
                                        sessionStore.showPopup({
                                            title: cacheStore.GetString("WarningLabel"),
                                            content: cacheStore.GetString("QuebecVaccinationRegistry_InvalidatedInMyleButStillActiveOnRvqMessage")
                                        });
                                }}
                                hideControlOperation={LocalPatientOperations.MedicalItems.ToggleInvalid}
                                extraButtons={this.latestCommittedMedicalItem && sessionStore.rvq.canSubmitToRvq(this.latestCommittedMedicalItem, false)
                                    ? [
                                        state => (
                                            <button
                                                disabled={state.formLocked || IsNullOrWhiteSpace(state.note)}
                                                className="btn btn-primary btn-sm"
                                                style={{ marginLeft: "5px" }}
                                                onClick={() => {
                                                    sessionStore.dispatch(LocalPatientOperations.MedicalItems.SaveValidationNoteForImmunization, {
                                                        medicalItemId: this.latestCommittedMedicalItem.MedicalItemId,
                                                        value: state.note
                                                    });
                                                }}
                                            >
                                                {cacheStore.GetString("SavePlusSyncLabel")}
                                            </button>
                                        )
                                    ]
                                    : []
                                }
                            />
                        )
                        : null
                    }
                    IsModifyButtonDisabled={this.isModifyButtonDisabled}
                    IsDeleteButtonHidden={(medicalItem: MedicalItemImmunization) => medicalItem.SubmittedToQuebecRegistryOn && medicalItem.IsRefused}
                    showFavorites={this.props.showFavorites}
                    onPrintButtonClick={cacheStore.ClinicSettings.EnablePrintPatientImmunizationCard && this.anyImmunizationToPrint() ? () => this.onPrint() : null}
                    printButtonDataCy="immunization-print-icon"
                />
            </div>
        );
    }

    private async getMedicationAndBatchModelForPreference(rvqPreference: RvqMedicationPreference): Promise<{ medication: MedicationModel; medicalBatch: MedicationBatchModel; }> {
        let medication: MedicationModel = null;
        let medicalBatch: MedicationBatchModel = null;
        if (rvqPreference && this.isRvqEnabled) {
            const medicationResponse = await Client.postAsync<GetRvqMedicationByTradeNameResponse>(ServerOperations.Server.QuebecVaccinationRegistry_GetMedicationByTradeName, { TradeName: rvqPreference.QuebecVaccinationRegistryId });
            medication = medicationResponse.data.Medication;
            medicalBatch = medication?.Batches.firstOrDefault(x => x.Id === rvqPreference?.LotId);
        }

        return { medication, medicalBatch };
    }

    private async setEditedItemState(medItem: MedicalItem): Promise<void> {
        let item = medItem as MedicalItemImmunization;

        if (!item || item.IsCollapsed || item.IsCommitted)
            return;

        let isSameItem = item.displayId == this.state.DisplayId && item.Id == this.state.Id;
        if (isSameItem)
            return;

        let medicationPreferences: RvqMedicationPreference[];
        let pracitionerPreferences: RvqPractitionerPreference[];
        let locationPreferences: RvqLocationPreference[];

        if (this.isRvqEnabled) {
            const preferencesResponse = await Client.postAsync<GetRvqPreferencesResponse>(ServerOperations.Server.QuebecVaccinationRegistry_GetPreferences, {});

            medicationPreferences = (preferencesResponse?.data?.MedicationPreferences ?? []).map(p => new RvqMedicationPreference(p));
            pracitionerPreferences = (preferencesResponse?.data?.PractitionerPreferences ?? []).map(p => new RvqPractitionerPreference(p));
            locationPreferences = (preferencesResponse?.data?.LocationPreferences ?? []).map(p => new RvqLocationPreference(p));
        }

        const { defaultMedication, defaultPractitioner, defaultLocation } = QuebecVaccinationRegistryHelper.getDefaultPreferences(
            medicationPreferences,
            pracitionerPreferences,
            locationPreferences
        );

        const { medication: defaultMedicationModel, medicalBatch: defaultMedicalBatchModel } = await this.getMedicationAndBatchModelForPreference(defaultMedication);
        let tradeName = "";
        this.mutateState(
            s => {
                const cacheData = sessionStore.getLockedData<Partial<MedicalItemsImmunizationViewState>>("ImmunizationModel");

                if (this.isRvqEnabled) {
                    s.rvqMedicationPreferences = medicationPreferences;
                    s.rvqLocationPreferences = locationPreferences;
                    s.rvqPractitionerPreferences = pracitionerPreferences;
                }

                s.Id = item.Id;
                s.DisplayId = item.displayId;

                if (item.IsNewEMRTemplate) {
                    s.RouteCode = defaultMedicalBatchModel?.DefaultRoute?.Code ?? cacheData?.RouteCode ?? "";
                    s.SelectedSiteLookupId = cacheData?.SelectedSiteLookupId ?? "";
                }
                else {
                    s.SelectedSiteLookupId = item.SiteLookupId;
                    s.RouteCode = !item.IsQuebecRegistryForm && this.isRvqEnabled
                        ? QuebecVaccinationRegistryHelper.convertMyleRouteToRvqRoute(item.RouteCode)
                        : item.RouteCode;
                }

                s.ImmunizationName = tradeName = s.ImmunizationNameId = item.IsNewEMRTemplate
                    ? cacheData?.ImmunizationName ?? defaultMedicationModel?.TradeName ?? item.Name
                    : item.Name;

                s.ImmunizingAgent = item.IsNewEMRTemplate
                    ? cacheData?.ImmunizingAgent ?? defaultMedicationModel?.Agent?.Label ?? item.Type
                    : item.Type;

                if (item.RefusedDate && !IsNullOrUndefined(item.RefusedPrecisionDate))
                    s.RefusalDate = new MedicalItemDate({
                        date: item.RefusedDate,
                        datePrecision: item.IsNewEMRTemplate ? null : item.RefusedPrecisionDate,
                        dateText: PatientMedicalItemsHelper.GetLifeStageDateString(item.RefusedDate, item.RefusedPrecisionDate),
                        isDateApproximative: item.RefusedPrecisionDate != LifeStageDatePrecisionEnum.Day
                    });

                if (item.AdministeredDate && !IsNullOrUndefined(item.AdministeredPrecisionDate))
                    s.AdministrationDate = new MedicalItemDate({
                        date: item.AdministeredDate,
                        datePrecision: item.AdministeredPrecisionDate,
                        dateText: PatientMedicalItemsHelper.GetLifeStageDateString(item.AdministeredDate, item.AdministeredPrecisionDate),
                        isDateApproximative: this.isRvqEnabled
                            ? ![LifeStageDatePrecisionEnum.DayApprox, LifeStageDatePrecisionEnum.Day].contains(item.AdministeredPrecisionDate)
                            : item.AdministeredPrecisionDate != LifeStageDatePrecisionEnum.Day
                    });

                s.LotNumber = item.IsNewEMRTemplate
                    ? cacheData?.LotNumber ?? defaultMedicalBatchModel?.LotNumber ?? item.LotNumber
                    : item.LotNumber;

                s.Manufacturer = item.IsNewEMRTemplate
                    ? cacheData?.Manufacturer ?? defaultMedicationModel?.ManufacturerCode ?? item.Manufacturer
                    : item.Manufacturer;

                s.Instruction = item.Instruction;
                s.IsRefused = item.IsRefused;
                s.Dose = item.IsNewEMRTemplate
                    ? cacheData?.Dose ?? defaultMedicalBatchModel?.DefaultQuantity ?? item.Dose
                    : item.Dose;

                s.Booster = item.IsNewEMRTemplate
                    ? cacheData?.Booster ?? item.BoosterInMonth
                    : item.BoosterInMonth;

                s.BoosterInMonths = item.IsNewEMRTemplate
                    ? cacheData?.Booster ?? item.BoosterInMonth
                    : item.BoosterInMonth;

                s.SelectedBoosterLookupId = item.IsNewEMRTemplate
                    ? cacheData?.SelectedBoosterLookupId ?? "m"
                    : "m";

                s.Notes = item.Notes;
                s.IsNewEMRTemplate = item.IsNewEMRTemplate;
                s.IsNewMedicalItem = item.IsNewMedicalItem;
                s.LifeStageStartDate = item.LifeStageDate;
                s.LifeStageIsStartDateApproximative = item.LifeStagePrecisionDate != LifeStageDatePrecisionEnum.Day;
                s.LifeStageDatePrecision = item.LifeStagePrecisionDate;
                s.LifeStageDateTextboxValue = s.LifeStageStartDate != null
                    ? PatientMedicalItemsHelper.GetLifeStageDateString(s.LifeStageStartDate, item.LifeStagePrecisionDate)
                    : "";

                s.LifeStageAgeAtOnset = item.LifeStageAge;
                s.LifeStageMode = item.LifeStageMode ?? s.LifeStageMode;
                s.LifeStageSelectedLifestageLookupId = item.LifeStageStatusLookupId ?? s.LifeStageSelectedLifestageLookupId;
                s.LifeStageAgeSelectedgeAtOnsetUnit = item.LifeStageAgeStatusLookupId ?? s.LifeStageAgeSelectedgeAtOnsetUnit;

                let mlDosageUnit: FlatLookupView;
                mlDosageUnit = s.AvailableDoseUnitLookups != null && (item.IsNewEMRTemplate || item.IsNewMedicalItem)
                    ? s.AvailableDoseUnitLookups.filter(x => x.LookupCode == "DU.mL").firstOrDefault()
                    : null;

                const doesUnitType = item.DoseUnitLookupId ?? mlDosageUnit?.LookupID ?? "";
                s.SelectedDoseUnitLookupId = item.IsNewEMRTemplate
                    ? cacheData?.SelectedDoseUnitLookupId ?? doesUnitType
                    : doesUnitType;

                s.QuebecRegistryId = !item.IsNewMedicalItem ? item.QuebecRegistryId : null;
                s.QuebecRegistryAdministeredReasonCode = item.QuebecRegistryAdministeredReasonCode;
                s.QuebecRegistryExpirationDate = item.QuebecRegistryExpirationDate;
                s.QuebecRegistryRefusalMotiveCode = item.QuebecRegistryRefusalMotiveCode;
                s.IsQuebecRegistryForm = item.IsQuebecRegistryForm;
                s.SubmittedToQuebecRegistryOn = item.SubmittedToQuebecRegistryOn;
                s.SubmittedToQuebecRegistryBy = item.SubmittedToQuebecRegistryBy;
                s.IsQuebecRegistryHistoricalVaccine = item.IsQuebecRegistryHistoricalVaccine;
                s.QuebecRegistrySiteCode = !item.IsQuebecRegistryForm && this.isRvqEnabled
                    ? QuebecVaccinationRegistryHelper.convertMyleSiteToRvqSite(item.SiteLookupId) ?? item.QuebecRegistrySiteCode
                    : item.QuebecRegistrySiteCode;

                s.currentForm = item.IsRefused
                    ? item.IsQuebecRegistryAllAntigensSelected
                        ? ImmunizationDetailEditForm.RefuseAllVaccines
                        : ImmunizationDetailEditForm.RefuseSpecificVaccine
                    : ImmunizationDetailEditForm.CreateImmunization;

                s.isCurrentlySaving = false;
                if (item.IsNewEMRTemplate) {
                    s.AdministeredByDisplayText = defaultPractitioner?.DisplayName ?? item.AdministeredByDisplayText ?? "";
                    s.QuebecRegistryAdministeredById = defaultPractitioner?.QuebecVaccinationRegistryId || item.QuebecRegistryAdministeredById;
                    s.VaccinatorEmployeeId = defaultPractitioner?.EmployeeId || item.VaccinatorEmployeeId;
                    s.LocationDisplayText = defaultLocation?.DisplayName ?? item.LocationDisplayText;
                    s.QuebecRegistryLocationId = defaultLocation?.QuebecVaccinationRegistryId || item.QuebecRegistryLocationId;
                    s.QuebecRegistryDoseUnitCode = cacheData?.QuebecRegistryDoseUnitCode ?? defaultMedicalBatchModel?.DefaultQuantityUnit.Code ?? item.QuebecRegistryDoseUnitCode;
                    s.QuebecRegistryLotId = cacheData?.QuebecRegistryLotId ?? defaultMedicalBatchModel?.Id ?? item.QuebecRegistryLotId;
                    s.QuebecRegistryAgentCode = cacheData?.QuebecRegistryAgentCode ?? defaultMedicationModel?.Agent?.Code ?? item.QuebecRegistryAgentCode;
                    s.QuebecRegistrySiteCode = cacheData?.QuebecRegistrySiteCode ?? item.QuebecRegistrySiteCode;
                }
                else {
                    s.AdministeredByDisplayText = item.AdministeredByDisplayText;
                    s.QuebecRegistryAdministeredById = item.QuebecRegistryAdministeredById;
                    s.VaccinatorEmployeeId = item.VaccinatorEmployeeId;
                    s.LocationDisplayText = item.LocationDisplayText;
                    s.QuebecRegistryLocationId = item.QuebecRegistryLocationId;
                    s.QuebecRegistryDoseUnitCode = !item.IsQuebecRegistryForm && this.isRvqEnabled
                        ? QuebecVaccinationRegistryHelper.convertMyleDoseUnitToRvqDoseUnits(item.DoseUnitLookupId) ?? item.QuebecRegistryDoseUnitCode
                        : item.QuebecRegistryDoseUnitCode;

                    s.QuebecRegistryLotId = item.QuebecRegistryLotId;
                    s.QuebecRegistryAgentCode = item.QuebecRegistryAgentCode;
                    s.QuebecRegistryAntigenCodes = item.QuebecRegistryAntigenCodes;
                    if (item.QuebecRegistryAntigenCodes) {
                        s.SelectedAntigens = QuebecVaccinationRegistryHelper.filterCodeValues(
                            QuebecVaccinationRegistryHelper.getCodeValues(KeyValuePair.fromMapOfStrings(s.RvqOptions?.antigens)),
                            item.QuebecRegistryAntigenCodes.split(",")
                        );
                    }
                    s.QuebecRegistrySiteCode = !item.IsQuebecRegistryForm && this.isRvqEnabled
                        ? QuebecVaccinationRegistryHelper.convertMyleSiteToRvqSite(item.SiteLookupId) ?? item.QuebecRegistrySiteCode
                        : item.QuebecRegistrySiteCode;
                }
            },
            async () => {
                if (!this.isRvqEnabled || !tradeName)
                    return;
                const validTradeName = await this.isValidTradeName(this.state.ImmunizationName);
                const batches = validTradeName
                    ? (await QuebecVaccinationRegistryHelper.getBatchesForMedication(this.state.ImmunizationName)).batches
                    : [];

                this.mutateState(s => {
                    s.RvqOptions.batches = batches;
                    s.showExpiredLotNumbers = batches.all(x => x.IsExpired);
                });
            }
        );
    }

    private async savePlus(): Promise<void> {
        await this.executeSave(true);
    }

    private async executeSave(isSavePlus: boolean = false): Promise<void> {
        let editMedicalItem = MyleHelper.CloneObject(
            this.props.Model
                .get("MedicalItemsList")
                .toArray()
                ?.firstOrDefault(x => x.IsCommitted == false) as MedicalItemImmunization
        );

        if (editMedicalItem != null) {
            editMedicalItem = new MedicalItemImmunization({
                ...editMedicalItem,
                LifeStageAge: this.state.LifeStageAgeAtOnset,
                LifeStageAgeStatusLookupId: this.state.LifeStageAgeSelectedgeAtOnsetUnit,
                LifeStageDate: cacheStore.Helper.CreateDateAsUTC(this.state.LifeStageStartDate),
                LifeStageMode: this.state.LifeStageMode,
                LifeStagePrecisionDate: this.state.LifeStageDatePrecision,
                LifeStageStatusLookupId: this.state.LifeStageSelectedLifestageLookupId,
                Name: this.state.ImmunizationName,
                Type: this.state.ImmunizingAgent,
                AdministeredDate: cacheStore.Helper.CreateDateAsUTC(this.state.AdministrationDate.date),
                RefusedDate: cacheStore.Helper.CreateDateAsUTC(this.state.RefusalDate.date),
                LotNumber: this.state.LotNumber,
                Manufacturer: this.state.Manufacturer,
                RouteCode: this.state.RouteCode,
                Instruction: this.state.Instruction,
                IsRefused: this.state.IsRefused,
                Dose: this.state.Dose,
                AdministeredPrecisionDate: this.state.AdministrationDate.datePrecision,
                RefusedPrecisionDate: this.state.RefusalDate.datePrecision,
                BoosterInMonth: this.state.BoosterInMonths,
                SiteLookupId: this.state.SelectedSiteLookupId,
                DoseUnitLookupId: this.state.Dose > 0 ? this.state.SelectedDoseUnitLookupId : "",
                Notes: this.state.Notes,
                AdministeredByDisplayText: this.state.AdministeredByDisplayText,
                LocationDisplayText: this.state.LocationDisplayText,
                QuebecRegistryId: this.state.QuebecRegistryId,
                QuebecRegistryAdministeredById: this.state.QuebecRegistryAdministeredById,
                QuebecRegistryLocationId: this.state.QuebecRegistryLocationId,
                QuebecRegistryDoseUnitCode: this.state.QuebecRegistryDoseUnitCode,
                QuebecRegistrySiteCode: this.state.QuebecRegistrySiteCode,
                QuebecRegistryLotId: this.state.QuebecRegistryLotId,
                QuebecRegistryAdministeredReasonCode: this.state.QuebecRegistryAdministeredReasonCode,
                QuebecRegistryAgentCode: this.state.QuebecRegistryAgentCode,
                QuebecRegistryExpirationDate: this.state.QuebecRegistryExpirationDate,
                QuebecRegistryRefusalMotiveCode: this.state.QuebecRegistryRefusalMotiveCode,
                IsQuebecRegistryForm: cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry,
                IsQuebecRegistryAllAntigensSelected: this.isRefuseAllVaccinesForm,
                QuebecRegistryAntigenCodes: this.getAntigenCodeAsString(),
                IsQuebecRegistryHistoricalVaccine: this.state.IsQuebecRegistryHistoricalVaccine,
                VaccinatorEmployeeId: this.state.VaccinatorEmployeeId
            });
        }

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter([editMedicalItem]);
        }

        editMedicalItem.ContextLookupId ||= this.props.Model.get("Encounter")?.ConsultationContextLookupId;

        if (sessionStore.isLockedData("ImmunizationModel"))
            sessionStore.setLockData("ImmunizationModel", MedicalItemsImmunizationViewState.getDataToLock(this.state));

        this.mutateState(
            s => {
                const currentAdministrator = s.rvqPractitionerPreferences.firstOrDefault(x => x.QuebecVaccinationRegistryId === s.QuebecRegistryAdministeredById);
                const currentLocation = s.rvqLocationPreferences.firstOrDefault(x => x.QuebecVaccinationRegistryId === s.QuebecRegistryLocationId);

                const now = moment().toDate();
                if (currentAdministrator)
                    currentAdministrator.LastAppliedDate = now;
                if (currentLocation)
                    currentLocation.LastAppliedDate = now;

                s.isCurrentlySaving = true;
            },
            () => {
                if (this.isRvqEnabled)
                    new RvqImmunizationSavingSyncManager(
                        this.props.PatientContactId,
                        this.props.PatientModel.PatientResponse.ContactFacet_id,
                        isSaving => this.mutateState(s => s.isCurrentlySaving = isSaving)
                    ).saveImmunization(
                        editMedicalItem,
                        PatientMedicalItemsHelper.getLatestMedicalItemIdForEMRTemplate(this.props.Model.get("MedicalItemsList").toArray()),
                        this.state.shouldSyncWithRvq,
                        isSavePlus
                    );
                else
                    sessionStore.dispatch(
                        LocalPatientOperations.MedicalItems.Save,
                        {
                            view: MedicalItemsTab.Immunization,
                            editedItems: [editMedicalItem],
                            isSavePlus
                        }
                    );
            }
        );
    }

    private async save(): Promise<void> {
        if (this.isRvqEnabled && this.state.IsRefused)
            sessionStore.showPopup({
                title: cacheStore.GetString("QuebecVaccinationRegistry_SyncARefusalLabel"),
                content: cacheStore.GetString("QuebecVaccinationRegistry_SyncRefusalVaccinationWarningLabel"),
                showCancel: true,
                okAction: () => this.executeSave(false)
            });
        else
            await this.executeSave(false);
    }

    private isSaveButtonEnabled(): boolean {
        const isCurentlySaving = this.isRvqEnabled
            ? this.state.isCurrentlySaving
            : this.props.Model.get("IsSaving");

        return (
            this.state.shouldSyncWithRvq
                ? this.checkIfSaveAndSyncIsEnabled()
                : this.checkIfSaveIsEnabled()
        )
            && !isCurentlySaving;
    }

    private checkIfSaveIsEnabled(): boolean {
        const isImmunizationNameFilled = this.state.ImmunizationName?.trim().length > 0;
        if (this.state.IsQuebecRegistryHistoricalVaccine && this.isCreateImmunizationForm)
            return isImmunizationNameFilled || this.agentFieldIsSet;

        if (!this.isRvqEnabled)
            return isImmunizationNameFilled;

        return this.isRefuseAllVaccinesForm || isImmunizationNameFilled || this.agentFieldIsSet;
    }

    private checkIfSaveAndSyncIsEnabled(): boolean {
        if (this.state.IsRefused)
            return this.checkIfSaveAndSyncRefusalIsEnabled();

        const isLotNumberValid =
            (
                this.state.IsQuebecRegistryHistoricalVaccine
                && !this.state.LotNumber
                && !this.state.QuebecRegistryLotId
            )
            || (
                !!this.state.LotNumber
                && !!this.state.QuebecRegistryLotId
                && this.state.RvqOptions.batches?.any(x => x.Id === this.state.QuebecRegistryLotId)
            );

        const isDoseValid =
            (
                this.state.IsQuebecRegistryHistoricalVaccine
                && (
                    !this.state.QuebecRegistryDoseUnitCode
                    || this.state.QuebecRegistryDoseUnitCode === ConstantValues.UnknownDoseUnitCode
                )
                && IsNullOrUndefined(this.state.Dose)
            )
            || (!!this.state.QuebecRegistryDoseUnitCode && this.state.Dose > 0);

        return (
            this.checkIfSaveIsEnabled()
            && (
                this.state.IsQuebecRegistryHistoricalVaccine
                || this.state.RvqOptions.tradeNames?.any(x => x.Item.Id === this.state.ImmunizationName)
            )
            && sessionStore.rvq.canEdit(this.state.SubmittedToQuebecRegistryBy, false)
            && MedicalItemDate.isValid(this.state.AdministrationDate, false, false)
            && (!!this.state.QuebecRegistryAdministeredById || !!this.state.VaccinatorEmployeeId)
            && !!this.state.QuebecRegistryLocationId
            && !!this.state.QuebecRegistryAgentCode
            && !!this.state.RvqOptions.agents[this.state.QuebecRegistryAgentCode]
            && !!this.state.ImmunizingAgent
            && isLotNumberValid
            && !!this.state.RouteCode
            && (!!this.state.QuebecRegistrySiteCode || !!this.state.SelectedSiteLookupId)
            && isDoseValid
            && (
                !this.state.QuebecRegistryId
                || (
                    !!this.state.SubmittedToQuebecRegistryOn
                    && !!this.state.Notes
                )
            )
            && this.administrationReasonIsValid
        );
    }

    private checkIfSaveAndSyncRefusalIsEnabled(): boolean {
        if (
            this.state.SubmittedToQuebecRegistryOn
            && !this.checkIfSaveIsEnabled()
        )
            return false;

        if (this.isRefuseAllVaccinesForm)
            return this.refuseAllVaccinesFormIsValid;

        return this.refuseSpecificVaccineFormIsValid;
    }

    private get isCreateImmunizationForm(): boolean {
        return this.state.currentForm === ImmunizationDetailEditForm.CreateImmunization;
    }

    private get isRefuseAllVaccinesForm(): boolean {
        return this.state.currentForm === ImmunizationDetailEditForm.RefuseAllVaccines;
    }

    private get refuseAllVaccinesFormIsValid(): boolean {
        return this.refusalMotiveCodeIsSet;
    }

    private get antigenFieldIsSet(): boolean {
        return this.state.SelectedAntigens?.any();
    }

    private get agentFieldIsSet(): boolean {
        return !!this.state.ImmunizingAgent?.trim();
    }

    private get refuseSpecificVaccineFormIsValid(): boolean {
        return this.antigenFieldIsSet
            && this.refusalMotiveCodeIsSet
            && this.agentFieldIsSet;
    }

    private get refusalMotiveCodeIsSet(): boolean {
        return !!this.state.QuebecRegistryRefusalMotiveCode;
    }

    private get administrationReasonIsValid(): boolean {
        let isImmunizationNameFilled = this.state.ImmunizationName?.trim().length > 0;
        if (this.state.IsQuebecRegistryHistoricalVaccine)
            isImmunizationNameFilled = true;

        const mustHaveAdministrationReasonCode = !!this.state.QuebecRegistryAgentCode && this.state.RvqOptions.agents[this.state.QuebecRegistryAgentCode]?.NeedsAdministrationReason;

        return isImmunizationNameFilled
            && (!mustHaveAdministrationReasonCode || !!this.state.QuebecRegistryAdministeredReasonCode);
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemImmunization[] {
        let medicalItems: MedicalItemImmunization[] = items as MedicalItemImmunization[]; // cast MedicalItem to immunization

        return medicalItems.filterSearch(this.props.Model.get("SearchTerm"), item => {
            // items cannot be null for search, replace them with empty string
            let name = item?.Name ?? "";
            let administeredDate = item?.AdministeredDate ?? "";
            let refusedDate = item?.RefusedDate ?? "";

            return `${name} ${administeredDate} ${refusedDate}`;
        });
    }

    /**
     * Handle the result of an authentication check with the Quebec Vaccination Registry (RVQ).
     * @param isAuthenticated Whether the user is authenticated with the RVQ.
     * @param showImmunizationProfile Whether to open the RVQ immunization profile if authentication succeeds.
     */
    private onRvqAuthChanged(isAuthenticated: boolean, showImmunizationProfile: boolean): void {
        this.mutateState(s => {
            s.isRvqAuthenticated = isAuthenticated;
            if (showImmunizationProfile)
                s.showImmunizationProfile = isAuthenticated;
            s.isRvqLoading = false;
        });
    }

    private getAntigenCodeAsString(): string {
        if (!this.isRvqEnabled || !this.state.SelectedAntigens?.any() || this.isRefuseAllVaccinesForm || !this.state.IsRefused)
            return null;

        return this.state.SelectedAntigens
            .map(x => x.Code)
            .join(", ");
    }

    private setViewVaccinationState(medicalItems: MedicalItemModel[]): void {
        const item = medicalItems.firstOrDefault() as MedicalItemImmunization;

        this.mutateState(s => {
            s.IsRefused = item.IsRefused;
            s.SubmittedToQuebecRegistryOn = item.SubmittedToQuebecRegistryOn;
        });
    }

    private get isModifyButtonDisabled(): boolean {
        return this.isRvqEnabled
            && this.state.IsRefused
            && !!this.state.SubmittedToQuebecRegistryOn;
    }

    private async onPrint(): Promise<void> {
        const response = await Client.postAsync<{ FileName: string }>(
            ServerOperations.Server.Myle_Patient_MedicalItem_GenerateImmunizationPdf,
            {
                PatientContactId: this.props.PatientContactId
            }
        );

        if (response.isSuccess)
            PrintHelper.ShowPrintPreviewFileName(response.data.FileName);
    }

    private anyImmunizationToPrint(): boolean {
        return !!this.props.Model
            .get("AllMedicalItemsList")
            .findEntry(item => !(item as MedicalItemImmunization).IsRefused);
    }
}

export { ImmunizationDetailEditForm, MedicalItemsImmunizationView, MedicalItemsImmunizationViewState };
